/**
 * A Postman-`pm`-flavored script sandbox for pre-request and test scripts.
 *
 * This is a *soft* sandbox: scripts run via `new Function(...)`, which gives
 * them their own lexical scope (no closure access to this module's
 * internals) but not hard process isolation — `window`, `fetch`, etc. are
 * still technically reachable, same as any inline `<script>`. That's an
 * honest tradeoff for a client-side, self-hosted dev tool: the trust
 * boundary is "your own scripts in your own browser tab against your own
 * APIs," not a multi-tenant server sandbox. True isolation would need a
 * Worker or iframe sandbox; not implemented here.
 */

export interface TestResult {
  name: string;
  passed: boolean;
  error: string | null;
  durationMs: number;
}

export interface ScriptRunResult {
  logs: Array<{ level: 'log' | 'info' | 'warn' | 'error'; message: string }>;
  tests: TestResult[];
  /** Variables the script set via pm.environment.set/pm.variables.set — merged into the send as request-level overrides. */
  variableChanges: Record<string, string>;
  /** A top-level (non-test) script error — syntax error or an uncaught throw outside pm.test(). */
  error: string | null;
}

export interface ScriptRequestContext {
  method: string;
  url: string;
  headers: Record<string, string>;
}

export interface ScriptResponseContext {
  code: number;
  status: string;
  responseTimeMs: number;
  headers: Record<string, string>;
  body: string;
}

interface RunScriptOptions {
  script: string;
  request: ScriptRequestContext;
  variables: Record<string, string | null>;
  response?: ScriptResponseContext;
}

class AssertionError extends Error {}

const stringify = (value: unknown): string => {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const deepEqual = (a: unknown, b: unknown): boolean => {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  const aKeys = Object.keys(a as Record<string, unknown>);
  const bKeys = Object.keys(b as Record<string, unknown>);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((key) =>
    deepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key]),
  );
};

/** A minimal chai-`expect`-compatible assertion chain covering the assertions Postman scripts commonly use. */
class Expectation {
  private negated = false;
  private readonly actual: unknown;

  constructor(actual: unknown) {
    this.actual = actual;
  }

  private assert(condition: boolean, message: string, negatedMessage: string): void {
    const pass = this.negated ? !condition : condition;
    if (!pass) {
      throw new AssertionError(this.negated ? negatedMessage : message);
    }
  }

  get to(): this {
    return this;
  }

  get be(): this {
    return this;
  }

  get been(): this {
    return this;
  }

  get have(): this {
    return this;
  }

  get an(): this {
    return this;
  }

  get is(): this {
    return this;
  }

  get and(): this {
    return this;
  }

  get not(): this {
    this.negated = !this.negated;
    return this;
  }

  get exist(): this {
    this.assert(
      this.actual !== null && this.actual !== undefined,
      `expected value to exist, got ${stringify(this.actual)}`,
      `expected value not to exist, got ${stringify(this.actual)}`,
    );
    return this;
  }

  get true(): this {
    this.assert(
      this.actual === true,
      `expected ${stringify(this.actual)} to be true`,
      `expected value not to be true`,
    );
    return this;
  }

  get false(): this {
    this.assert(
      this.actual === false,
      `expected ${stringify(this.actual)} to be false`,
      `expected value not to be false`,
    );
    return this;
  }

  get null(): this {
    this.assert(
      this.actual === null,
      `expected ${stringify(this.actual)} to be null`,
      `expected value not to be null`,
    );
    return this;
  }

  get undefined(): this {
    this.assert(
      this.actual === undefined,
      `expected ${stringify(this.actual)} to be undefined`,
      `expected value not to be undefined`,
    );
    return this;
  }

  get empty(): this {
    const value = this.actual;
    const isEmpty =
      value === '' ||
      (Array.isArray(value) && value.length === 0) ||
      (typeof value === 'object' && value !== null && Object.keys(value).length === 0);
    this.assert(
      isEmpty,
      `expected ${stringify(value)} to be empty`,
      `expected ${stringify(value)} not to be empty`,
    );
    return this;
  }

  equal(expected: unknown): this {
    this.assert(
      this.actual === expected,
      `expected ${stringify(this.actual)} to equal ${stringify(expected)}`,
      `expected ${stringify(this.actual)} not to equal ${stringify(expected)}`,
    );
    return this;
  }

  eql(expected: unknown): this {
    this.assert(
      deepEqual(this.actual, expected),
      `expected ${stringify(this.actual)} to deeply equal ${stringify(expected)}`,
      `expected ${stringify(this.actual)} not to deeply equal ${stringify(expected)}`,
    );
    return this;
  }

  above(expected: number): this {
    this.assert(
      typeof this.actual === 'number' && this.actual > expected,
      `expected ${stringify(this.actual)} to be above ${expected}`,
      `expected ${stringify(this.actual)} not to be above ${expected}`,
    );
    return this;
  }

  below(expected: number): this {
    this.assert(
      typeof this.actual === 'number' && this.actual < expected,
      `expected ${stringify(this.actual)} to be below ${expected}`,
      `expected ${stringify(this.actual)} not to be below ${expected}`,
    );
    return this;
  }

  a(type: string): this {
    const actualType = Array.isArray(this.actual) ? 'array' : typeof this.actual;
    this.assert(
      actualType === type,
      `expected ${stringify(this.actual)} to be a ${type}`,
      `expected ${stringify(this.actual)} not to be a ${type}`,
    );
    return this;
  }

  include(expected: unknown): this {
    const value = this.actual;
    const included =
      (typeof value === 'string' && typeof expected === 'string' && value.includes(expected)) ||
      (Array.isArray(value) && value.some((item) => deepEqual(item, expected))) ||
      (typeof value === 'object' &&
        value !== null &&
        typeof expected === 'string' &&
        Object.prototype.hasOwnProperty.call(value, expected));
    this.assert(
      included,
      `expected ${stringify(value)} to include ${stringify(expected)}`,
      `expected ${stringify(value)} not to include ${stringify(expected)}`,
    );
    return this;
  }

  property(name: string, expectedValue?: unknown): this {
    const has = typeof this.actual === 'object' && this.actual !== null && name in this.actual;
    this.assert(
      has,
      `expected ${stringify(this.actual)} to have property "${name}"`,
      `expected ${stringify(this.actual)} not to have property "${name}"`,
    );
    if (has && expectedValue !== undefined) {
      const actualValue = (this.actual as Record<string, unknown>)[name];
      this.assert(
        deepEqual(actualValue, expectedValue),
        `expected property "${name}" to equal ${stringify(expectedValue)}, got ${stringify(actualValue)}`,
        `expected property "${name}" not to equal ${stringify(expectedValue)}`,
      );
    }
    return this;
  }

  lengthOf(length: number): this {
    const actualLength =
      Array.isArray(this.actual) || typeof this.actual === 'string'
        ? this.actual.length
        : undefined;
    this.assert(
      actualLength === length,
      `expected length ${stringify(actualLength)} to equal ${length}`,
      `expected length not to equal ${length}`,
    );
    return this;
  }

  match(pattern: RegExp): this {
    this.assert(
      typeof this.actual === 'string' && pattern.test(this.actual),
      `expected ${stringify(this.actual)} to match ${pattern}`,
      `expected ${stringify(this.actual)} not to match ${pattern}`,
    );
    return this;
  }

  /** Postman-style sugar: `pm.response.to.have.status(200)`. */
  status(expected: number): this {
    return this.equal(expected);
  }
}

const buildResponseHelper = (response: ScriptResponseContext) => ({
  code: response.code,
  status: response.status,
  responseTime: response.responseTimeMs,
  headers: {
    get: (name: string) =>
      response.headers[name] ??
      response.headers[
        Object.keys(response.headers).find((key) => key.toLowerCase() === name.toLowerCase()) ?? ''
      ],
    has: (name: string) =>
      Object.keys(response.headers).some((key) => key.toLowerCase() === name.toLowerCase()),
    all: () => ({ ...response.headers }),
  },
  text: () => response.body,
  json: () => JSON.parse(response.body),
  to: new Expectation(response.code),
});

export const runScript = ({
  script,
  request,
  variables,
  response,
}: RunScriptOptions): ScriptRunResult => {
  const trimmed = script.trim();
  const result: ScriptRunResult = { logs: [], tests: [], variableChanges: {}, error: null };

  if (trimmed === '') return result;

  const variableStore: Record<string, string | null> = { ...variables };

  const consoleShim = {
    log: (...args: unknown[]) =>
      result.logs.push({ level: 'log', message: args.map(stringify).join(' ') }),
    info: (...args: unknown[]) =>
      result.logs.push({ level: 'info', message: args.map(stringify).join(' ') }),
    warn: (...args: unknown[]) =>
      result.logs.push({ level: 'warn', message: args.map(stringify).join(' ') }),
    error: (...args: unknown[]) =>
      result.logs.push({ level: 'error', message: args.map(stringify).join(' ') }),
  };

  const variableScope = {
    get: (key: string) => variableStore[key] ?? undefined,
    set: (key: string, value: unknown) => {
      const stringValue = typeof value === 'string' ? value : stringify(value);
      variableStore[key] = stringValue;
      result.variableChanges[key] = stringValue;
    },
    unset: (key: string) => {
      delete variableStore[key];
    },
    has: (key: string) => key in variableStore,
    toObject: () => ({ ...variableStore }),
  };

  const pm = {
    environment: variableScope,
    variables: variableScope,
    globals: variableScope,
    collectionVariables: variableScope,
    request: {
      method: request.method,
      url: request.url,
      headers: {
        get: (name: string) =>
          request.headers[name] ??
          request.headers[
            Object.keys(request.headers).find((k) => k.toLowerCase() === name.toLowerCase()) ?? ''
          ],
        has: (name: string) =>
          Object.keys(request.headers).some((k) => k.toLowerCase() === name.toLowerCase()),
      },
    },
    response: response ? buildResponseHelper(response) : undefined,
    test: (name: string, fn: () => void) => {
      const start = performance.now();
      try {
        fn();
        result.tests.push({
          name,
          passed: true,
          error: null,
          durationMs: performance.now() - start,
        });
      } catch (error) {
        result.tests.push({
          name,
          passed: false,
          error: error instanceof Error ? error.message : String(error),
          durationMs: performance.now() - start,
        });
      }
    },
    expect: (actual: unknown) => new Expectation(actual),
  };

  try {
    const runner = new Function('pm', 'console', `'use strict';\n${script}`);
    runner(pm, consoleShim);
  } catch (error) {
    result.error = error instanceof Error ? error.message : String(error);
  }

  return result;
};
